/**
 * Massachusetts outline, projected to a 900 x 560 viewBox
 * (Mercator, fitted — includes Cape Cod, the Islands and the harbor detail).
 */
export const MA_PATH =
  "M787.6,517.8L798.6,524.2L822.9,522.9L838.1,518.3L848.2,499.5L861.6,527.7L860.8,535.7L852.4,540L824.6,538.9L790.1,522.9ZM657.3,504.4L672.4,504.4L688.4,480.2L711.1,464.1L720.4,468.1L726.2,486.9L738.8,494.2L747.3,483.4L746.4,506.0L711.9,505.7L685.9,508.1L670.7,520.7ZM88.0,65.0L276.4,71.0L443.7,77.3L549.6,80.6L558.1,68.0L575.7,67.7L574.9,50.7L587.5,40.9L603.5,45.8L611.0,28.8L641.3,20L653.9,28.2L660.6,24.7L662.3,44.4L669.9,81.7L690.9,94.6L701.0,83.1L712.8,92.4L699.3,117L680.0,118.9L648.0,128.5L658.1,142.1L643.0,155.8L637.1,153.3L632.9,169.4L629.5,158.2L620.3,172.7L629.5,192.8L618.6,190.1L614.4,197.7L621.1,204.8L616.9,212.2L628.7,216.0L633.7,206.2L645.5,204.0L653.1,216.8L673.3,224.4L683.4,235.8L685.0,248.3L702.7,273.6L701.9,287.1L696.0,297.7L685.9,299.0L688.4,305.5L706.1,319.3L716.1,317.2L722.9,323.7L729.6,346.1L725.4,359.6L736.3,372.6L755.7,382.0L784.3,385.2L791.8,391.4L807.8,379.6L823.8,377.4L846.5,368.5L851.5,361.8L850.7,340.4L845.6,323.9L834.7,332.3L834.7,306.1L830.5,291.2L817.9,282.0L810.3,283.9L806.1,294.4L796.9,278.4L807.8,275.5L825.5,280.3L844.8,296.1L859.9,329.3L867.5,361.5L869.2,398.5L856.6,433.2L854.9,445.0L849.0,442.6L859.1,412.5L853.2,406.3L831.3,407.6L795.2,418.4L770.0,416.2L764.1,423.8L749.8,425.7L737.2,442.3L709.4,445.0L680.8,462.8L667.4,475.4L649.7,482.9L630.4,486.9L635.4,480.0L664.9,470.8L678.3,458.2L698.5,445.0L702.7,434.3L701.0,402.8L706.1,391.7L684.2,384.7L684.2,400.6L678.3,396.8L674.9,410.3L663.2,409.5L664.9,418.1L638.8,421.1L632.9,434.3L634.6,446.1L629.5,453.9L608.5,460.1L598.4,455.5L590.0,459.3L587.5,408.4L572.4,403.6L565.6,392.5L557.2,379.6L541.2,370.7L537.0,355.6L538.7,333.7L528.6,335.3L528.6,295.5L431.1,299.0L430.2,294.2L260.4,290.6L241.9,291.7L206.6,290.1L204.0,300.7L192.3,302.3L193.1,290.1L147.7,289.3L48.5,285.5L35.9,285.8L30.8,274.4L59.4,167.0Z";

export const MAP_WIDTH = 900;
export const MAP_HEIGHT = 560;

/** Projected city anchors inside the same viewBox. */
export const CITIES = {
  boston: [604.4, 187.7],
  cambridge: [592.5, 183.4],
  worcester: [430.2, 218.6],
  springfield: [245.7, 269.5],
  lowell: [544.1, 100.8],
  brockton: [613.9, 275.2],
  newBedford: [633.6, 415.9],
  lynn: [630.0, 153.8],
  fallRiver: [581.9, 395.4],
  pittsfield: [92.2, 159.1],
  framingham: [520.7, 213.3],
  lawrence: [580.0, 77.4],
  haverhill: [600.1, 55.3],
  taunton: [597.2, 333.0],
  barnstable: [783.1, 395.8],
  northampton: [233.7, 198.8],
  fitchburg: [430.2, 116.8],
  attleboro: [551.3, 319.0],
  plymouth: [696.2, 314.6],
  salem: [642.4, 137.1],
  quincy: [617.7, 221.6],
} as const satisfies Record<string, readonly [number, number]>;

export const MAYA_HOUSE: readonly [number, number] = [601.3, 206.2];

export type MapNode = {
  /** city key */
  at: keyof typeof CITIES;
  /** 0 - 1, relative intensity of this data point */
  weight: number;
};

export type Dataset = {
  id: string;
  /** design-token name, e.g. "glow-teal" */
  tone: "glow-teal" | "glow-gold" | "glow-coral";
  stat: string;
  body: string;
  source: string;
  nodes: MapNode[];
};

// Housing crisis, close to home. Worcester-focused figures (client-supplied,
// source-verified where noted); the map weights Worcester heaviest for the local ones.
export const DATASETS: Dataset[] = [
  {
    id: "worcester-homeless",
    tone: "glow-coral",
    stat: "3,110",
    body: "people were experiencing homelessness in Worcester County on a single January night in 2025, a record high.",
    source: "Central Mass Housing Alliance · 2025 Point-in-Time Count",
    nodes: [
      { at: "worcester", weight: 1 },
      { at: "fitchburg", weight: 0.6 },
      { at: "framingham", weight: 0.34 },
      { at: "boston", weight: 0.3 },
      { at: "springfield", weight: 0.26 },
      { at: "lowell", weight: 0.24 },
    ],
  },
  {
    id: "homeless-rise",
    tone: "glow-gold",
    stat: "+20%",
    body: "increase in Worcester County homelessness in a single year.",
    source: "Central Mass Housing Alliance · 2025 Point-in-Time Count",
    nodes: [
      { at: "worcester", weight: 1 },
      { at: "fitchburg", weight: 0.55 },
      { at: "framingham", weight: 0.3 },
      { at: "boston", weight: 0.28 },
      { at: "springfield", weight: 0.24 },
    ],
  },
  {
    id: "worcester-burden",
    tone: "glow-teal",
    stat: "11,500+",
    body: "Worcester renters are severely cost-burdened, spending more than half their income on housing.",
    source: "Worcester Regional Research Bureau",
    nodes: [
      { at: "worcester", weight: 1 },
      { at: "boston", weight: 0.5 },
      { at: "springfield", weight: 0.45 },
      { at: "fitchburg", weight: 0.42 },
      { at: "brockton", weight: 0.4 },
      { at: "lowell", weight: 0.4 },
      { at: "newBedford", weight: 0.38 },
      { at: "fallRiver", weight: 0.36 },
      { at: "lawrence", weight: 0.34 },
    ],
  },
  {
    id: "eli-shortage",
    tone: "glow-gold",
    stat: "180,000+",
    body: "affordable homes Massachusetts needs for its extremely low-income renters.",
    source: "NLIHC · MAPC, Massachusetts",
    nodes: [
      { at: "boston", weight: 1 },
      { at: "worcester", weight: 0.8 },
      { at: "springfield", weight: 0.72 },
      { at: "lowell", weight: 0.55 },
      { at: "brockton", weight: 0.6 },
      { at: "newBedford", weight: 0.58 },
      { at: "fallRiver", weight: 0.5 },
      { at: "lawrence", weight: 0.5 },
      { at: "lynn", weight: 0.5 },
      { at: "fitchburg", weight: 0.44 },
      { at: "taunton", weight: 0.4 },
      { at: "quincy", weight: 0.4 },
      { at: "barnstable", weight: 0.3 },
      { at: "pittsfield", weight: 0.28 },
    ],
  },
];

/** Design-token tones mapped to Maya House palette colors. */
export const TONES: Record<Dataset['tone'], string> = {
  'glow-teal': '#5fe0cf',
  'glow-gold': '#f2c46b',
  'glow-coral': '#f2a98d',
}
export const GLOW_CORAL = '#f2a98d'
